import React from 'react';
import { useTranslation } from 'react-i18next';
import { Site } from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import { IconName } from '@lib/components/Modal/enums';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import PageOutOfRangeEmptyState from '@lib/components/PageOutOfRangeEmptyState/PageOutOfRangeEmptyState';
import SitePreview from 'components/SitePreview/SitePreview';
import Pagination from '@lib/components/Pagination/Pagination';
import { ListQueryHookResult } from 'utils/fetch/types';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import styles from './SectorSites.module.scss';

type Props = {
  onEditHandler: () => void;
  sitesQueryHookResult: ListQueryHookResult<Site>;
};

export default function SectorSites({
  onEditHandler,
  sitesQueryHookResult,
}: Props) {
  const { t } = useTranslation();
  const { loading, collection, totalCount, firstLoading } =
    sitesQueryHookResult;

  const isContentVisible = !!totalCount && !!collection.length;
  const isEmptyStateVisible = !isContentVisible && !firstLoading;
  const isEmptyStateByOutOfRangePage = isEmptyStateVisible && !!totalCount;
  const isPaginationVisible =
    !!totalCount && !!collection.length && totalCount > collection.length;

  if (firstLoading) {
    return <CircleLoader />;
  }

  if (isEmptyStateByOutOfRangePage) {
    return <PageOutOfRangeEmptyState totalEntries={totalCount} />;
  }

  if (isEmptyStateVisible) {
    return (
      <EmptyState
        title={t('no-linked-sector-sites-yet')}
        text={t('no-linked-sector-sites-text')}
        icon={IconName.Cancel}
        actions={[
          <Button
            key="edit"
            buttonText={t('edit-sector')}
            buttonType={ButtonTypes.primaryFilled}
            onClick={onEditHandler}
          />,
        ]}
      />
    );
  }

  return (
    <CircleLoaderWrapper
      isLoading={loading}
      type={CircleLoaderWrapperType.blur}
    >
      <div className={styles.sitesGrid}>
        {collection.map((site) => (
          <div key={site.id}>
            <SitePreview {...site} bordered />
          </div>
        ))}
      </div>
      {isPaginationVisible && (
        <div className={styles.pagination}>
          <Pagination totalEntries={totalCount} />
        </div>
      )}
    </CircleLoaderWrapper>
  );
}
